<template>
  <Teleport :to="teleportTarget" v-if="teleportTarget">
    <div class="catalog-modal-wrapper outfit-font">
      <Transition name="modal-fade">
        <div v-if="dialog" class="custom-modal-overlay" @click.self="$emit('close')">
          <div
            class="custom-modal-container global-radius"
            :class="{ 'custom-modal-fullscreen': fullscreen }"
            :style="containerStyle"
          >
            <v-card class="d-flex flex-column h-100 border-0" elevation="0">
              <v-toolbar dark :color="color ? color : 'info'" class="flex-grow-0">
                <template v-if="$slots.toolbar">
                  <slot name="toolbar"/>
                </template>
                <template v-else>
                  <v-toolbar-title :text="title" />
                  <v-btn icon='mdi-close' dark @click="$emit('close')" />
                </template>
              </v-toolbar>

              <div class="custom-modal-content flex-grow-1 overflow-y-auto">
                <slot></slot>
              </div>

              <div class="custom-modal-footer flex-grow-0 d-flex pa-2 border-t" :style="{ backgroundColor: footer_color }" v-if="$slots.footer">
                <slot name="footer"/>
              </div>
            </v-card>
          </div>
        </div>
      </Transition>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { ref, watch, onMounted, computed } from 'vue'

const props = defineProps<{
  visible?: boolean
  title?: string
  fullscreen?: boolean
  max_width?: string | number
  max_height?: string | number
  color?: string
  footer_color?: string
}>()

const emit = defineEmits<{
  (e: 'close'): void
  (e: 'onmount'): void
}>()

const dialog = ref(false)
const teleportTarget = ref<Element | null>(null)

const containerStyle = computed(() => {
  if (props.fullscreen) return {}

  let maxWidth = props.max_width || '700px'
  if (typeof maxWidth === 'string' && /^\d+$/.test(maxWidth)) {
    maxWidth = `${maxWidth}px`
  } else if (typeof maxWidth === 'number') {
    maxWidth = `${maxWidth}px`
  }

  let maxHeight = props.max_height || '90vh'
  if (typeof maxHeight === 'string' && /^\d+$/.test(maxHeight)) {
    maxHeight = `${maxHeight}px`
  } else if (typeof maxHeight === 'number') {
    maxHeight = `${maxHeight}px`
  }

  return {
    maxWidth,
    maxHeight,
    width: '100%',
  }
})

onMounted(() => {
  const host = document.getElementById('one-800-products-modal-host')
  if (host && host.shadowRoot) {
    teleportTarget.value = host.shadowRoot.getElementById('app-modal-root')
  }

  dialog.value = !!props.visible
  emit('onmount')
})

watch(() => props.visible, (newVal) => {
  dialog.value = !!newVal
})
</script>

<style scoped lang="scss">
.catalog-modal-wrapper {
  // Reset style inheritance from host page body
  line-height: normal;
  box-sizing: border-box;

  *, *:before, *:after {
    box-sizing: inherit;
  }
}

.custom-modal-overlay {
  position: fixed !important;
  top: 0 !important;
  left: 0 !important;
  width: 100vw !important;
  height: 100vh !important;
  background-color: rgba(0, 0, 0, 0.8) !important;
  z-index: 999999 !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  padding: 24px !important;
  box-sizing: border-box !important;
}

.custom-modal-container {
  background: #ffffff !important;
  border-radius: 12px !important;
  overflow: hidden !important;
  display: flex !important;
  flex-direction: column !important;
  box-shadow: 0 24px 38px 3px rgba(0,0,0,0.14), 0 9px 46px 8px rgba(0,0,0,0.12), 0 11px 15px -7px rgba(0,0,0,0.2) !important;
  animation: modal-scale-up 0.25s cubic-bezier(0.25, 0.8, 0.25, 1) !important;
}

.custom-modal-fullscreen {
  width: 100vw !important;
  height: 100vh !important;
  max-width: 100vw !important;
  max-height: 100vh !important;
  border-radius: 0 !important;
  padding: 0 !important;
}

.custom-modal-content {
  padding: 8px !important;
  box-sizing: border-box !important;
}

/* Animations */
@keyframes modal-scale-up {
  from {
    transform: scale(0.9);
    opacity: 0;
  }
  to {
    transform: scale(1);
    opacity: 1;
  }
}

.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.25s ease;
}

.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
}
</style>
